<template>
  <div class="container-fluid">
    <div class="container">
      <div class="section section--small">
        <ValidationObserver
          ref="observer"
          v-slot="{ invalid }"
          tag="form"
          @submit.prevent="addToBasket()"
        >
          <div class="steps">
            <ValidationObserver
              class="steps__step"
              v-for="(formStep, index) in productConfigFields"
              v-slot="{ valid }"
              :key="index"
              @click="focusStep(index)"
            >
              <div
                class="steps__header"
                :class="[
                  { 'steps__header--active': focusedStep === index },
                  { 'steps__header--completed': valid },
                ]"
              >
                <div class="steps__number">{{ stepNumber(formStep.name) }}</div>
                <div class="steps__title">
                  <span v-if="formStep.name">{{ formStep.name }}</span>
                  <span v-else>Pliki</span>
                </div>
              </div>
              <div class="steps__body">
                <div
                  class="steps__form"
                  @click="focusStep(index)"
                  :class="[
                    { 'steps__form--active': focusedStep === index },
                    { 'steps__form--completed': valid },
                  ]"
                >
                  <div class="steps__topic">
                    {{ formStep.children ? formStep.children.name : "Pliki" }}
                  </div>
                  <div
                    class="w-100"
                    v-for="(groupFields, index) in getFields(formStep)"
                    :key="index"
                  >
                    <basket-field :uploading-files="uploadingFiles" :refs="$refs" :field="groupFields" @field-change="saveConfiguration" @upload-file="uploadAttachment" @remove-file="removeFile" />
                  </div>
                </div>
              </div>
            </ValidationObserver>
          </div>
          <div class="steps__additional">
            <div class="steps__submit">
              <div
                class="link m-right-4"
                @click="clearForm()"
                :class="{ 'link--disabled': clearingForm }"
              >
                Wyczyść formularz
              </div>
              <ButtonPrimary
                :disabled="invalid || disabled || checkFileStatus"
                class="button--big button--semibold button--fontsize"
              >
                <span v-if="checkFileStatus">Trwa przetwarzanie plików...</span
                ><span v-else>Gotowe</span></ButtonPrimary
              >
            </div>
          </div>
        </ValidationObserver>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import "@/assets/scss/pages/order";
</style>

<style lang="scss">
@import "@/assets/scss/components/form/form";
</style>

<script>
import { ValidationObserver, extend } from "vee-validate";
import { required } from "vee-validate/dist/rules";
import { mapActions, mapGetters } from "vuex";
import { actionTypes } from "@/store/modules/Products/action-types";
import { getterTypes } from "@/store/modules/Products/getter-types";
import { actionTypes as cartActionTypes } from "@/store/modules/Cart/action-types";
import { getterTypes as cartGetterTypes } from "@/store/modules/Cart/getter-types";
import { validateMessages } from "@/config";
import { requiredMsg } from "@/validations";

import axios from "axios";
import { apiURL } from "@/config";
import { v4 as uuidv4 } from "uuid";

import { cartToken, getFilesStatus } from "@/services/cart";

extend("required", {
  ...required,
  message: requiredMsg,
});

extend('validateFileList', {
  validate: value => {
    if (!Array.isArray(value) || value.length === 0) {
      return false;
    }
    return value.every(file => file.hasOwnProperty('path'));
  },
  message: 'Przynajmniej jeden plik jest wymagany i każdy plik musi mieć ścieżkę' // Wiadomość wyświetlana w przypadku niepowodzenia walidacji
});

export default {
  name: "Basket",
  metaInfo: {
    titleTemplate: "%s - Konfiguracja produktu",
  },
  props: {
    productCode: {
      type: String,
    },
    configCode: {
      type: String,
    },
    item: {
      type: Object,
      default: null,
    },
    itemId: {
      default: null,
    },
  },
  components: {
    ButtonPrimary: () => import("@/pages/components/buttons/ButtonPrimary"),
    ValidationObserver,
    BasketField: () => import("./BasketField.vue")
  },
  async created() {
    const code = {
      productCode: this.productCode,
      configCode: this.configCode,
    };    if (this.itemId) {
      const response = await this.ADD_CART_ITEM(this.itemId);
      const data = {
        itemId: this.itemId,
        productCode: this.productCode,
        configCode: this.configCode,
        body: response.configuration.values,
      };
      await this.SAVE_PRODUCT_CODE_CONFIG(data);
    } else {
      await this.FETCH_PRODUCT_CODE_CONFIG(code);
    }


  },
  data() {
    return {
      ownValueString: "Własna wartość",
      focusedStep: 0,
      attachments: [],
      files: [],
      disabled: false,
      loading: false,
      clearingForm: false,
      uploadingFiles: [],
      checkFileStatus: false,
      configuration: {}
    };
  },
  computed: {
    productConfigFiles() {
      const configuration = this.collectConfiguration();
      let fileFields = {};
      for (const key in configuration) {
        if (configuration[key].type === 'file') {
          fileFields[key] = configuration[key];
        }
      }

      return fileFields;
    },
    productConfigFields() {
      if (this.productConfig && this.productConfig.id) {
        return this.fillConfigurationModel();
      }
      return false;
    },
    variantOptions() {
      return this.productConfig.options;
    },
    productConfig() {
      return this.GET_PRODUCT_CODE_CONFIG();
    },
    cartItem() {
      return this.GET_CART_ITEM();
    },
  },
  methods: {
    ...mapActions("CartModule", [
      cartActionTypes.ADD_TO_CART,
      cartActionTypes.ADD_UPDATE_CART,
      cartActionTypes.ADD_CART_ITEM,
    ]),
    ...mapGetters("CartModule", [cartGetterTypes.GET_CART_ITEM]),
    ...mapActions("ProductsModule", [
      actionTypes.FETCH_PRODUCT_CODE_CONFIG,
      actionTypes.SAVE_PRODUCT_CODE_CONFIG,
      actionTypes.DELETE_FILE_PRODUCT_CODE_CONFIG,
      actionTypes.ADD_VARIANT,
    ]),
    ...mapGetters("ProductsModule", [
      getterTypes.GET_PRODUCT_CODE_CONFIG,
      getterTypes.GET_VARIANT,
    ]),
    async clearForm() {
      if (this.clearingForm) {
        return false;
      }

      this.clearingForm = true;
      const data = {
        productCode: this.productCode,
        configCode: this.configCode,
        body: [],
      };

      await this.SAVE_PRODUCT_CODE_CONFIG(data)
        .then(() => {
          this.$notify({
            group: validateMessages.group,
            type: "success",
            text: "Dane z formularza zostały wyczyszczone.",
          });
        })
        .catch(() => {
          this.$notify({
            group: validateMessages.group,
            type: "error",
            text: "Wystąpił błąd podczas czyszczenia danych.",
          });
        });

      if (this.productConfigFiles) {
        const promises = [];
        const dataFiles = {
          productCode: this.productCode,
          configCode: this.configCode,
          body: {},
        };
        await Object.keys(this.productConfigFiles).forEach(async (file) => {
          dataFiles.body.field_name = file.toString();

          if (this.itemId) {
            dataFiles.body.order_item_id = parseInt(this.itemId);
          }

          promises.push(
            this.DELETE_FILE_PRODUCT_CODE_CONFIG(dataFiles)
              .then(() => {
                return true;
              })
              .catch(() => {
                return false;
              })
          );
        });

        Promise.all(promises).then((response) => {
          const error = response.find((el) => el === false);
          if (error) {
            this.$notify({
              group: validateMessages.group,
              type: "error",
              text: "Wystąpił błąd podczas usuwania plików z&nbsp;formularza.",
            });
          } else {
            this.$notify({
              group: validateMessages.group,
              type: "success",
              text: "Pliki z formularza zostały usunięte.",
            });
          }
        });
      }

      this.clearingForm = false;
    },
    fillConfigurationModel() {
      console.log('fillConfigurationModel');
      const fields = this.productConfig.fields;

      for (const step in fields) {
        const actualStep = fields[step];
        if (actualStep.children) {
          for (const subStep in actualStep.children) {
            const actualSubStep = actualStep.children[subStep];
            if (actualSubStep.children) {
              const fields = actualSubStep.children;
              fields.forEach((field) => {
                const fieldData = this.getValueForFieldFromCustomerData(
                  field.id
                );
                if (fieldData) {
                  // field.value = fieldData;

                  if (fieldData.path) {
                    field.path = fieldData.path;
                  }
                } else {
                  if (this.fieldIsCustomSelect(field.type)) {
                    field.value = {
                      name: "",
                      value: "",
                    };
                  }
                }

                if (
                  (this.fieldIsCompound(field.type) ||
                    this.fieldIsCustomSelect(field.type)) &&
                  !this.isOwnValueString(
                    field.values[field.values.length - 1].name
                  )
                ) {
                  field.values.push({
                    firstValue: "",
                    name: this.ownValueString,
                    secondValue: "",
                  });
                }
              });
            }
          }
        }
      }

      return fields;
    },
    stepNumber(stepName) {
      return stepName.split(" ").find(Number);
    },
    getValueForFieldFromCustomerData(fieldId) {
      if (this.productConfig.customerData) {
        const customerDataValues = this.productConfig.customerData.fields;
        if (customerDataValues && customerDataValues[fieldId]) {
          return customerDataValues[fieldId];
        }
        return false;
      }
      return false;
    },
    flattenConfiguration(node) {
      let configuration = {};
      if (node.children) {
        node.children.forEach(child => {
          configuration = Object.assign(configuration, this.flattenConfiguration(child));
        });
      } else if(node.value) {
        console.log(node.value)
        configuration[node.id] = node.value;

        if (node.custom) {
          configuration[node.id].custom = node.custom;
        }
      }

      return configuration;
    },
    collectConfiguration() {
      let configuration = {};
      for (const key in this.productConfig.fields) {
        configuration = Object.assign(configuration, this.flattenConfiguration(this.productConfig.fields[key]));
      }

      return configuration;
    },
    async addToBasket() {
      const isValid = await this.$refs.observer.validate();
      if (!isValid) {
        return;
      }

      await this.saveConfiguration();

      const configuration = this.collectConfiguration();
      let options = {};

      Object.keys(configuration).forEach((id) => {
        if (
          configuration[id].value &&
          configuration[id].value.name &&
          configuration[id].value.value
        ) {
          this.variantOptions.forEach((variant) => {
            variant.values.forEach((variantOption) => {
              if (
                variantOption.value === configuration[id].value.value &&
                variantOption.name === configuration[id].value.name
              ) {
                options[variant.slug] = variantOption.value;
              }
            });
          });
        }
      });

      if (this.itemId) {
        let data = {
          id: this.itemId,
          body: {
            configuration,
            quantity: this.cartItem.quantity,
          },
        };

        if (Object.keys(options).length) {
          data.body.options = options;
        }

        await this.ADD_UPDATE_CART(data)
          .then(() => {
            this.$router.push({ name: "OrderSummary", params: { lang: "pl" } });
          })
          .catch(() => {
            this.$notify({
              group: validateMessages.group,
              type: "error",
              title: validateMessages.error.header,
              text: validateMessages.error.text,
            });
          });
      } else {
        let data = {
          productCode: this.productCode,
          quantity: 1,
          configuration,
        };

        if (Object.keys(options).length) {
          data.options = options;
        }

        await this.ADD_TO_CART(data)
          .then(() => {
            this.$router.push({ name: "OrderSummary" });
          })
          .catch(() => {
            this.$notify({
              group: validateMessages.group,
              type: "error",
              title: validateMessages.error.header,
              text: validateMessages.error.text,
            });
          });
      }
    },
    async saveConfiguration() {
      const configuration = this.collectConfiguration();

      const data = {
        productCode: this.productCode,
        configCode: this.configCode,
        body: configuration,
      };
      await this.SAVE_PRODUCT_CODE_CONFIG(data);
    },
    async uploadAttachment($event, field) {
      if ($event.target.files && $event.target.files.length > 0) {
        this.$notify({
          group: validateMessages.group,
          type: "info",
          text: `Trwa wgrywanie pliku...`,
        });

        for (let i = 0; i < $event.target.files.length; i++) {
          if ($event.target.files[i]) {
            this.files[field.id] = [];
            this.files[field.id].push($event.target.files[i]);
          }
          const file = $event.target.files[i];
          const formData = new FormData();
          formData.append("file", file);
          formData.append("field_name", field.id.toString());
          if (this.itemId) {
            formData.append("order_item_id", parseInt(this.itemId));
          }

          const data = {
            productCode: this.productCode,
            configCode: this.configCode,
            body: formData,
          };

          const token = cartToken();
          const uuid = uuidv4();

          this.uploadingFiles.push({
            name: file.name,
            uuid,
            progress: 0,
          });

          const config = {
            headers: {
              "Content-Type": "multipart/form-data",
            },
            onUploadProgress: (progressEvent) => {
              const percent = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total
              );
              const index = this.uploadingFiles.findIndex(
                (file) => file.uuid === uuid
              );
              this.uploadingFiles[index].progress = percent;
            },
          };

          axios
            .post(
              `${apiURL}carts/${token}/file/${data.productCode}/config/${data.configCode}?X-Progress-ID=${uuid}`,
              data.body,
              config
            )
            .then(async () => {
              const index = this.uploadingFiles.findIndex(
                (file) => file.uuid === uuid
              );
              this.uploadingFiles.splice(index, 1);
              const codes = {
                productCode: this.productCode,
                configCode: this.configCode,
              };
              if (this.itemId) {
                codes.itemId = this.itemId;
              }
              await this.FETCH_PRODUCT_CODE_CONFIG(codes);

              if (i + 1 === $event.target.files.length) {
                this.$notify({
                  group: validateMessages.group,
                  type: "success",
                  text: "Wgrywanie zakończone",
                });
              }

              this.runCheckFileStatus();

              this.$refs["fieldFile" + field.id].forEach((fieldFile, key) => {
                const valid = fieldFile.validate(
                  this.files[field.id][key].name
                );
                if (!valid) {
                  return;
                }
              });
            })
            .catch(() => {
              this.$notify({
                group: validateMessages.group,
                type: "error",
                text: "Wystąpił błąd podczas wgrywania plików",
              });
              return;
            });
        }
      }
      return;
    },
    runCheckFileStatus() {
      this.checkFileStatus = true;

      const data = {
        productCode: this.productCode,
        configCode: this.configCode,
      };

      if (this.itemId) {
        data.body.order_item_id = parseInt(this.itemId);
      }

      if (this.checkFileStatus) {
        const interval = setInterval(async () => {
          getFilesStatus(data)
            .then((response) => {
              let stopInterval = true;

              Object.keys(response.data).forEach((fileId) => {
                response.data[fileId].forEach((file) => {
                  if (file.status !== "completed") {
                    stopInterval = false;
                  } else {
                    this.checkFileStatus = true;
                  }
                });
              });

              if (stopInterval) {
                this.checkFileStatus = false;
                const codes = {
                  productCode: this.productCode,
                  configCode: this.configCode,
                };
                if (this.itemId) {
                  codes.itemId = this.itemId;
                }

                this.FETCH_PRODUCT_CODE_CONFIG(codes);
                clearInterval(interval);
              }
            })
            .catch(() => {
              this.$notify({
                group: validateMessages.group,
                type: "error",
                text:
                  "Wystąpił błąd podczas sprawdzania statusu pliku. <b>Zgłoś błąd do administracji.",
              });
            });
        }, 5000);
      }
    },
    async removeFile(fieldId, filePath) {
      if (this.loading) {
        return false;
      }
      this.loading = true;
      this.$notify({
        group: validateMessages.group,
        type: "info",
        text: "Trwa usuwanie pliku...",
      });

      let data = {
        productCode: this.productCode,
        configCode: this.configCode,
        body: { field_name: fieldId.toString(), path: filePath },
      };

      if (this.itemId) {
        data.body.order_item_id = parseInt(this.itemId);
      }

      this.DELETE_FILE_PRODUCT_CODE_CONFIG(data)
        .catch(() => {})
        .finally(() => {
          this.loading = false;
        });
    },
    getFields(fields) {
      return fields.children ? fields.children : fields;
    },
    isOwnValueString(string) {
      return string === this.ownValueString;
    },
    fieldIsVariant(fieldType) {
      return fieldType === "variant";
    },
    fieldIsSelect(fieldType) {
      return fieldType === "choice";
    },
    fieldIsCustomSelect(fieldType) {
      return fieldType === "choice_with_custom";
    },
    fieldIsCompound(fieldType) {
      return fieldType === "compound";
    },
    fieldIsInput(fieldType) {
      return fieldType === "input";
    },
    fieldIsFile(fieldType) {
      return fieldType === "file";
    },
    focusStep(step) {
      this.focusedStep = step;
    },
  },
};
</script>
